<template>
  <q-card
    flat
    :dark="isDarkModeGlobal"
  >
    <q-card-section class="q-gutter-xs">
      <q-input
        :dark="isDarkModeGlobal"
        v-if="!isManualBrainPressureTransmission && deratingParameters"
        outlined
        dense
        readonly
        step="0.000001"
        label="Pressure transmission [-]"
        type="number"
        hide-bottom-space
        :rules="[
          val => !!val || 'This field is required',
          val => val > 0 || 'Value must be > 0',
          val => val <= 1 || 'Value must be <= 1'
        ]"
        v-model="brain_pressure_transmission"
      >
        <template #append>
          <q-icon
            name="edit"
            @click="calculatorStore.switchToManualBrainPressureTransmission(true)"
          >
            <q-tooltip>
              Set your own value for brain pressure transmission
            </q-tooltip>
          </q-icon>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="brainPressureTransmissionMessage"
          />
        </template>
      </q-input>
      <q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        v-else-if="isManualBrainPressureTransmission && deratingParameters"
        filled
        dense
        step="0.000001"
        label="Pressure transmission [-]"
        type="number"
        hide-bottom-space
        v-model="brain_pressure_transmission_manual"
        @update:modelValue="validateAndExecuteCalculation(brain_pressure_transmission_manual, 'pr', 'Pressure transmission [-]')"

        :rules="[
          val => !!val || 'This field is required',
          val => val > 0 || 'Value must be > 0',
          val => val <= 1 || 'Value must be <= 1'
        ]"
      >
        <template #append>
          <q-icon
            name="refresh"
            @click="calculatorStore.switchToManualBrainPressureTransmission(false)"
          >
            <q-tooltip>
              Use brain pressure transmission calculated value
            </q-tooltip>
          </q-icon>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="brainPressureTransmissionMessage"
          />
        </template>
      </q-input>

      <q-input
        :dark="isDarkModeGlobal"
        v-if="deratingParameters && !isManualBrainAttenuationCoeff"
        outlined
        dense
        readonly
        label="Attenuation coefficient [dB/cm/MHz]"
        type="number"
        v-model="attenuation_coefficient"
        hide-bottom-space
      >
        <template #append>
          <q-icon
            name="edit"
            @click="calculatorStore.switchToManualBrainAttenuationCoeff(true)"
          >
            <q-tooltip>
              Use brain attenuation coefficient calculated value
            </q-tooltip>
          </q-icon>
          <reference-popup-proxy
            :link="'https://doi.org/10.13099/VIP21000-04-1'"
          />
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="attenuationCoeffMessage"
          />
        </template>
      </q-input>

      <q-input
        v-if="deratingParameters && isManualBrainAttenuationCoeff"
        :dark="isDarkModeGlobal"
        filled
        dense
        label="Attenuation coefficient [dB/cm/MHz]"
        type="number"
        v-model="brain_attenuation_coeff_manual"
        hide-bottom-space
        @update:modelValue="validateAndExecuteCalculation(brain_attenuation_coeff_manual, 'pr', 'Attenuation coefficient [dB/cm/MHz]')"
      >
        <template #append>
          <q-icon
            name="refresh"
            @click="calculatorStore.switchToManualBrainAttenuationCoeff(false)"
          >
            <q-tooltip>
              Use brain attenuation coefficient calculated value
            </q-tooltip>
          </q-icon>
          <reference-popup-proxy

            :link="'https://doi.org/10.13099/VIP21000-04-1'"
          />
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="attenuationCoeffMessage"
          />
        </template>
      </q-input>


      <q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        outlined
        dense
        readonly
        label="Absorption attenuation coefficient [db/cm/MHz]"
        type="number"
        v-model="att_coef_abs"
        hide-bottom-space
      >
        <template #append>
          <temp-rise-equation-dialog />
        </template>
      </q-input>
    </q-card-section>
  </q-card>
</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {computed, ref} from "vue"
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue"
import TempRiseEquationDialog from "./TempRiseEquationDialog.vue"
const calculatorStore = useCalculatorStore()
const {
  focal_depth_mm,
  thickness_of_skull_mm,
  thickness_of_scalp_mm,
  coupling_thickness_mm,
  isDarkModeGlobal,
  att_coef_abs,
  brain_pressure_transmission,
  attenuation_coefficient,
  brain_pressure_transmission_manual,
  isManualBrainPressureTransmission,
  brain_attenuation_coeff_manual,
  isManualBrainAttenuationCoeff,
  loadingValue
} = storeToRefs(calculatorStore)

const deratingParameters = ref(true)
const brainPressureTransmissionMessage = `Proportion of pressure transmitted in the brain expressed as a number from 0 to 1, with 0 representing complete attenuation and 1 representing no attenuation. Estimated based on entered fundamental frequency, attenuation coefficient and distance that the beam travels in the brain derived from entered coupling thickness, scalp thickness, skull thickness and focal depth. When modified, pressure transmission calculations based on entered fundamental frequency, attenuation coefficient and distance that the beam travels in the brain derived from entered coupling thickness, scalp thickness, skull thickness and focal depth is overruled. Use the reset button to undo the overruling.`
const attenuationCoeffMessage = `The attenuation coefficient reflects the decrease in ultrasound pressure as it travels through a medium, expressed in decibels per unit distance (in cm), per unit fundamental frequency (in MHz). The attenuation coefficient depends on the medium; for example, it is higher for bone compared to soft tissues, indicating that bone attenuates ultrasound more than soft tissues. The default is based on estimates reported in the literature (see reference) and is set to a relatively low value. Consequently, the pressure decrease is relatively small and the Isspa estimate is relatively high. This default was chosen to represent a the worst-case scenario in terms of safety. Please note that a different default attenuation coefficient value is used for estimating temperature rise. When modified, the calculation of the brain pressure transmission will be based on the manual input. Use the reset button to undo the overruling.`
const minimumValueFocalDepth = computed(() => +coupling_thickness_mm.value + +thickness_of_scalp_mm.value + +thickness_of_skull_mm.value)
const blurActiveInput = () => {
  const activeElement = document.activeElement;
  if (activeElement && activeElement.tagName === 'INPUT') {
    activeElement.blur()
  }
};

let debounceTimeout = null // Timeout reference for debounce
let interval = null // Timeout reference for interval
const loading = ref(false) // Tracks spinner visibility

let totalTime = 3000
let intervalTime = 100 // Time between each count

function validateAndExecuteCalculation(val: number, calcType: string, label: string) {
  if (label === 'Pressure transmission [-]') {
    // val is brain_pressure_transmission_manual
    if (!!val && val > 0 && val <= 1) {

      // check linked attenuation coefficient to be valid
      if (attenuation_coefficient.value >= 0) {
        executeCalculation(val, calcType)
      }

    }
  }

  if (label === 'Attenuation coefficient [dB/cm/MHz]') {
    //  val is brain_attenuation_coeff_manual
    if (!!val && val > 0) {

      // check linked attenuation coefficient to be valid
      if (brain_pressure_transmission.value >= 0) {
        executeCalculation(val, calcType)
      }
    }
  }
}

function executeCalculation(val: number, calcType: string) {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    clearInterval(interval)
    loadingValue.value = 0
  }

  loading.value = true;
  interval = setInterval(() => {
  loadingValue.value += 0.1;
    if (loadingValue.value > 3) {
      clearInterval(interval); // Stop the interval when count reaches 100
      loadingValue.value = 0
    }
  }, intervalTime);
      // Start debounce timer
  debounceTimeout = setTimeout(() => {
        // After debounce delay
    loading.value = false;
    if (val > 0 && focal_depth_mm.value >= minimumValueFocalDepth.value) {
      switch (calcType) {
        case 'acoustic_power':
          calculatorStore.updateByAcousticPower()
          break
        case 'transducer_efficiency':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'electrical_power':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'pr':
          calculatorStore.updateByPr()
          break
        case 'MI':
          calculatorStore.updateByMI()
          break
        case 'non_derated_MI':
          calculatorStore.updateByNonDeratedMI()
          break
        default:
          calculatorStore.updateByIsppa()
          break
      }
      calculatorStore.clearNotifications()

      blurActiveInput()
    } else {
      console.error("empty value or 0 not allowed")
    }
  }, totalTime);
}
</script>